// src/components/ProtectedRoute.jsx
import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Icon from "./AppIcon";
import EmployerAccessNotice from "./ui/EmployerAccessNotice";
import { EMPLOYER_PORTAL_DISABLED } from "config/employerAccess";

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Only enable maintenance in production environment, not UAT/preview
  // VITE_VERCEL_ENV will be "production" for production and "preview" for UAT
  const isProductionDeployment = import.meta.env.VITE_VERCEL_ENV === "production";
  const recruiterMaintenanceEnabled = false;
    // isProductionDeployment &&
    // (import.meta.env.VITE_RECRUITER_MAINTENANCE === "true" ||
    //   import.meta.env.VITE_RECRUITER_MAINTENANCE === "1");

  // Always call hooks at the top level!
  const roleCheck = useMemo(() => {
    if (allowedRoles.length === 0) {
      return { allowed: true };
    }
    const userRoleName = user?.role_name || user?.role;
    const userRole =
      typeof userRoleName === "string"
        ? userRoleName.toLowerCase()
        : String(userRoleName).toLowerCase();
    const normalizedAllowedRoles = allowedRoles.map((role) =>
      typeof role === "string" ? role.toLowerCase() : String(role).toLowerCase()
    );
    return {
      allowed: normalizedAllowedRoles.includes(userRole),
      userRole: user?.role_name || user?.role,
    };
  }, [user?.role, user?.role_name, allowedRoles]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="Loader2"
            size={48}
            className="animate-spin text-primary mx-auto mb-4"
          />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login-screen" state={{ from: location }} replace />;
  }

  // Determine normalized role once user is authenticated
  const userRoleRaw = user?.role_name || user?.role;
  const normalizedUserRole = userRoleRaw
    ? typeof userRoleRaw === "string"
      ? userRoleRaw.toLowerCase()
      : String(userRoleRaw).toLowerCase()
    : "";

  if (recruiterMaintenanceEnabled && normalizedUserRole === "recruiter") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-xl w-full bg-white shadow-lg rounded-xl p-6 text-center border border-secondary-200">
          <Icon name="Wrench" size={64} className="text-warning mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-text-primary mb-3">
            Scheduled Maintenance
          </h2>
          <p className="text-text-secondary mb-4">
            We’re upgrading recruiter uploads right now. Please pause
            submissions until maintenance is complete.
          </p>
          <p className="text-sm text-text-tertiary mb-6">
            This prevents accidental data loss while the document storage
            service is being refactored.
          </p>
          <div className="bg-warning-50 border border-warning-200 rounded-md px-4 py-3 text-left text-sm text-warning-800">
            <p className="font-semibold mb-1">What you can do</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Prepare required documents locally.</li>
              <li>Check back once maintenance is lifted.</li>
              <li>Contact CLAB support if you need urgent assistance.</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const isEmployerRoute = location.pathname.startsWith("/employer");

  if (EMPLOYER_PORTAL_DISABLED && isEmployerRoute) {
    return <EmployerAccessNotice variant="page" />;
  }

  // If role is not allowed, show access denied instead of redirecting
  if (!roleCheck.allowed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon name="ShieldX" size={64} className="text-error mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            Access Denied
          </h2>
          <p className="text-text-secondary mb-4">
            You don't have permission to access this page.
          </p>
          <div className="bg-secondary-100 rounded-lg p-4 mb-6">
            <p className="text-sm text-text-secondary">
              <strong>Your role:</strong> {roleCheck.userRole || "Unknown"}
            </p>
            <p className="text-sm text-text-secondary">
              <strong>Required roles:</strong> {allowedRoles.join(", ")}
            </p>
          </div>
          <button
            onClick={() => {
              // Navigate to appropriate dashboard based on user role_name
              const userRole = user.role_name || user.role;
              const role =
                typeof userRole === "string"
                  ? userRole.toLowerCase()
                  : String(userRole).toLowerCase();

              const roleRedirects = {
                admin: "/master-admin-dashboard",
                master_admin: "/master-admin-dashboard",
                clab: "/clab-dashboard",
                clab_user: "/clab-dashboard",
                recruiter: "/recruiter-biodata-management",
                employer: "/employer-biodata-selection",
              };
              const redirectPath = roleRedirects[role] || "/clab-dashboard";
              window.location.href = redirectPath;
            }}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
